import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiRequest } from "../helpers/api";
import { showError } from "../components/ToastNotifications";
import { Can } from "../components/AccessContext";
import InboxView from "../components/messages/InboxView";
import AutomationView from "../components/messages/AutomationView";
import { SavedRepliesView, ScheduledView } from "../components/messages/RepliesViews";
import "./Leads.css";
import "./Messages.css";

const tabs = [["inbox", "Inbox", "ti-inbox"], ["automations", "Automations", "ti-bolt"], ["replies", "Saved replies", "ti-message-2"], ["scheduled", "Scheduled", "ti-clock"]];

const Messages = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = tabs.some(([key]) => key === searchParams.get("tab")) ? searchParams.get("tab") : "inbox";
  const conversationParam = searchParams.get("conversation");
  const [capabilities, setCapabilities] = useState({ accounts: [], messaging: [], automation: [] });
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(true);
  const [savedReplies, setSavedReplies] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [rules, setRules] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState({ replies: true, scheduled: true, rules: true });
  const [startNewRule, setStartNewRule] = useState(false);

  const load = useCallback(async (key, path, apply) => {
    try { apply(await apiRequest(path)); }
    catch (error) { showError(error.message || "Unable to load part of the inbox."); }
    finally { setLoading((current) => ({ ...current, [key]: false })); }
  }, []);
  const loadCapabilities = useCallback(async () => {
    try { const result = await apiRequest("/api/dm/capabilities"); setCapabilities({ accounts: result?.accounts || [], messaging: result?.messaging || [], automation: result?.automation || [] }); }
    catch (error) { showError(error.message || "Unable to load connected accounts."); }
    finally { setCapabilitiesLoading(false); }
  }, []);
  const loadScheduled = useCallback(() => load("scheduled", "/api/dm/scheduled", (result) => setScheduled(result?.scheduledMessages || [])), [load]);
  const loadLogs = useCallback(() => load("logs", "/api/dm/automation-logs", (result) => setLogs(result?.logs || [])), [load]);

  useEffect(() => {
    loadCapabilities();
    load("replies", "/api/dm/saved-replies", (result) => setSavedReplies(result?.savedReplies || []));
    load("rules", "/api/dm/rules", (result) => setRules(result?.rules || []));
    loadScheduled();
    loadLogs();
  }, [load, loadCapabilities, loadScheduled, loadLogs]);

  const setTab = (key, extra = {}) => setSearchParams((current) => {
    const next = new URLSearchParams(current);
    if (key === "inbox") next.delete("tab"); else next.set("tab", key);
    if (key !== "inbox") next.delete("conversation");
    for (const [name, value] of Object.entries(extra)) { if (value) next.set(name, value); else next.delete(name); }
    return next;
  });
  const setConversation = useCallback((id) => setSearchParams((current) => { const next = new URLSearchParams(current); if (id) next.set("conversation", String(id)); else next.delete("conversation"); return next; }, { replace: true }), [setSearchParams]);
  const failedSchedules = scheduled.filter((item) => item.status === "FAILED").length;
  const activeRules = rules.filter((rule) => rule.enabled).length;

  return <section className="messages-page">
    <header className="msg-heading">
      <div><p className="leads-eyebrow">Customer conversations</p><h1>Messages &amp; Automations</h1><p>{capabilitiesLoading ? "Checking connected accounts…" : `${capabilities.messaging.length} connected account${capabilities.messaging.length === 1 ? "" : "s"} · ${activeRules} active automation${activeRules === 1 ? "" : "s"}`}</p></div>
      <Can permission="user.messages.manage"><button type="button" className="leads-primary-button" onClick={() => { setTab("automations"); setStartNewRule(true); }}><i className="ti ti-plus" />Create Automation</button></Can>
    </header>
    <nav className="leads-tabs" aria-label="Messages sections">{tabs.map(([key, label, icon]) => <button type="button" key={key} className={tab === key ? "is-active" : ""} aria-current={tab === key ? "page" : undefined} onClick={() => setTab(key)}>
      <i className={`ti ${icon}`} />{label}
      {key === "scheduled" && failedSchedules > 0 && <span className="leads-tab-count is-alert" aria-label={`${failedSchedules} failed`}>{failedSchedules}</span>}
    </button>)}</nav>

    {tab === "inbox" && <InboxView capabilities={capabilities} savedReplies={savedReplies} initialConversationId={conversationParam} onConversationChange={setConversation} onScheduledChange={loadScheduled} />}
    {tab === "automations" && <AutomationView rules={rules} setRules={setRules} logs={logs} reloadLogs={loadLogs} loading={loading.rules} savedReplies={savedReplies} startNew={startNewRule} onStartNewHandled={() => setStartNewRule(false)} />}
    {tab === "replies" && <SavedRepliesView savedReplies={savedReplies} setSavedReplies={setSavedReplies} loading={loading.replies} />}
    {tab === "scheduled" && <ScheduledView scheduled={scheduled} setScheduled={setScheduled} loading={loading.scheduled} onOpenConversation={(id) => setTab("inbox", { conversation: id })} />}
  </section>;
};

export default Messages;
